import { Input, Tab, Tabs, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { hideKeyboard, hideKeyboardOnPressEnter } from "@src/appV2/lib/utils/capacitor";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { ALL_QUALIFICATIONS_CATEGORY } from "@src/appV2/Qualifications/Categories/constants";
import { type QualificationsByCategories } from "@src/appV2/Qualifications/Categories/types";
import startCase from "lodash/startCase";
import { type ForwardedRef, forwardRef, useEffect, useMemo, useState } from "react";

import { type WorkerRequirement } from "../../Documents/api/types";
import { AddRoleRow } from "./AddRoleRow";

interface AddRolePageContentProps {
  qualificationsByCategories: QualificationsByCategories;
  autoApprovedQualifications: Set<string>;
  workerQualificationIds: string[];
  onCreateShoppableQualification: (qualificationId: string) => Promise<void>;
  qualificationNameToCredentialMap: Map<string, WorkerRequirement[]>;
}

function AddRolePageContentBase(props: AddRolePageContentProps, ref: ForwardedRef<HTMLDivElement>) {
  const {
    qualificationsByCategories,
    autoApprovedQualifications,
    workerQualificationIds,
    onCreateShoppableQualification,
    qualificationNameToCredentialMap,
  } = props;

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce({ value: searchQuery, debounceTimeInMs: 300 });

  const [addingQualificationIds, setAddingQualificationIds] = useState<Set<string>>(new Set());

  const { showErrorToast } = useToast();

  const allCategories = Object.keys(qualificationsByCategories);

  const [selectedCategory, setSelectedCategory] = useState<string>(
    allCategories[0] ?? ALL_QUALIFICATIONS_CATEGORY
  );

  const selectedCategoryQualifications = qualificationsByCategories[selectedCategory];

  const filteredQualifications = useMemo(() => {
    const rawFilteredQualifications = selectedCategoryQualifications.filter((qualification) => {
      const { displayLabel, description } = qualification.attributes;
      const qualificationSearchString = `${displayLabel.toLowerCase()} ${description.toLowerCase()}`;

      return qualificationSearchString.includes(debouncedSearchQuery.toLowerCase());
    });

    return rawFilteredQualifications.sort((a, b) =>
      a.attributes.displayLabel.localeCompare(b.attributes.displayLabel)
    );
  }, [selectedCategoryQualifications, debouncedSearchQuery]);

  useEffect(() => {
    if (isDefined(ref) && "current" in ref) {
      ref.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [filteredQualifications, ref]);

  return (
    <>
      <Box sx={{ paddingBottom: 3 }}>
        <Input
          value={searchQuery}
          placeholder="Search for a role"
          iconType="search"
          onChange={(event) => {
            setSearchQuery(event.target.value);
          }}
          onKeyPress={hideKeyboardOnPressEnter}
          onFocus={() => {
            setSelectedCategory(ALL_QUALIFICATIONS_CATEGORY);
          }}
        />
      </Box>

      {allCategories.length > 1 && (
        <Box sx={{ paddingBottom: 2 }}>
          <Tabs
            variant="underlined"
            value={selectedCategory}
            onChange={(_, newValue) => {
              setSelectedCategory(newValue as string);
            }}
          >
            {Object.keys(qualificationsByCategories).map((category) => (
              <Tab
                key={category}
                label={startCase(category)}
                value={category}
                variant="underlined"
              />
            ))}
          </Tabs>
        </Box>
      )}

      <Stack
        ref={ref}
        sx={{ flexDirection: "column", gap: 7, overflow: "auto", paddingY: 5, paddingTop: 0 }}
        onScroll={hideKeyboard}
      >
        {filteredQualifications.map((qualification) => {
          const qualificationName = qualification.attributes.name;
          const credentialRequirements = qualificationNameToCredentialMap.get(qualificationName);
          const requiresBaseCredential = isDefined(
            credentialRequirements?.find((workerRequirement) => workerRequirement.level === "STATE")
          );

          return (
            <AddRoleRow
              key={qualification.id}
              qualificationName={qualification.attributes.displayLabel}
              requiresLicense={!autoApprovedQualifications.has(qualification.attributes.name)}
              requiresBaseCredential={requiresBaseCredential}
              isAdded={workerQualificationIds.includes(qualification.id)}
              isAdding={addingQualificationIds.has(qualification.id)}
              onAdd={async () => {
                setAddingQualificationIds((previous) => {
                  const newSet = new Set(previous);
                  newSet.add(qualification.id);
                  return newSet;
                });

                try {
                  await onCreateShoppableQualification(qualification.id);
                } catch (maybeError) {
                  showErrorToast("Something went wrong adding new role. Please try again.");

                  throw maybeError;
                } finally {
                  setAddingQualificationIds((previous) => {
                    const newSet = new Set(previous);
                    newSet.delete(qualification.id);
                    return newSet;
                  });
                }
              }}
            />
          );
        })}

        {filteredQualifications.length === 0 && (
          <Text
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              paddingY: 10,
              textAlign: "center",
            })}
          >
            No roles matching your search
          </Text>
        )}
      </Stack>
    </>
  );
}

export const AddRolePageContent = forwardRef(AddRolePageContentBase);
